// Fetching a level, tilesets and all.
//
// Split from the parser so the parser stays pure and testable without a
// network: the interesting cases in a `.tmj` are all malformed maps, and none
// of them need a fetch to reproduce.

import { RUN_CONFIG, type RunKind } from "src/engine/game/modes";
import { faultMessage, type LevelSetup, validateLevel } from "src/engine/rules/campaign/level";
import { type ComposedLevel, composeLevel, fragmentPath, placeFragment } from "./fragments";
import { LevelParseError, parseLevel, type TmjMap, type TmjTileset } from "./tmj";

/**
 * Every scenario that ships.
 *
 * **A registry, not a campaign.** This used to be "every level, in the order a
 * run plays them", which quietly promised one continuous world that a run walks
 * through chapter by chapter — and that promise costs something on every level
 * added: a global map has to stay geographically coherent, its difficulty curve
 * is a property of the *sequence* rather than of any one map, and dropping a new
 * scenario in the middle renumbers everything after it.
 *
 * A scenario is self-contained instead: its own board, its own opening, its own
 * objectives, and its own answer to whether finishing it ends the run
 * (`LevelSetup.final`, a map property in Tiled). Adding one is adding a file and
 * a row here. Nothing before it changes, and nothing after it exists.
 *
 * What a run is, then, is *a scenario plus what the wave machine does when the
 * authored part runs out* — which is what the code already did, described
 * honestly. The ordering that used to be implied here belongs to whatever picks
 * a scenario, and right now that is `opening`.
 *
 * Named `SCENARIOS` and not `LEVELS` deliberately: `src/engine/ai/difficulty.ts`
 * exports a `LEVELS` of its own, meaning easy/normal/hard, and two things named
 * for one word in a file that imports both is how somebody grades a board.
 */
export interface Scenario {
  id: string;
  /** What the front door calls it. The map's own `levelName` is what the shelf shows. */
  name: string;
  /** One line on what this scenario is about, for a picker that does not exist yet. */
  blurb: string;
  /** Where a fresh run starts. Exactly one, asserted by `openingScenario`. */
  opening?: boolean;
}

export const SCENARIOS: readonly Scenario[] = [
  {
    id: "first-light",
    name: "First Light",
    blurb:
      "Your king, some scrap, and a camp behind an approach. The smallest board there will ever be.",
    opening: true
  },
  {
    id: "outer-frame",
    name: "Outer Frame",
    blurb:
      "A square room with two corners gated off. Dig the gate open and both corners open at " +
      "once, with an army waiting in each."
  },
  {
    id: "training",
    name: "Training",
    blurb:
      "Training Mode's map (§31 G1): a stockpile in your king's hands, two drawn camps, " +
      "and the run ends when both are beaten."
  }
];

export type LevelId = (typeof SCENARIOS)[number]["id"];

/**
 * Where a fresh run starts.
 *
 * A property of the table rather than a string in `App.tsx`, which is where it
 * was — so the app knew the name of one specific map and adding a second meant
 * editing a component. Throws rather than falling back: a build with no opening
 * scenario cannot start a run at all, and finding that out at the front door
 * beats finding it out from a blank board.
 */
export function openingScenario(): Scenario {
  const opening = SCENARIOS.find((entry) => entry.opening);
  if (!opening) throw new Error("no opening scenario — see SCENARIOS");
  return opening;
}

/**
 * Absolute, because a tileset's `source` is resolved *against* it.
 *
 * `import.meta.env.BASE_URL` is a path like `/`, and `new URL(source, "/...")`
 * throws "Invalid base URL" — a relative string is not a base. Fetch is happy
 * with a relative URL, so the level itself loads and only the tilesets fail,
 * which is a confusing place to discover it.
 */
const base = (): string =>
  new URL(`${import.meta.env.BASE_URL}levels/`, window.location.href).toString();

/**
 * Resolve `source` tilesets into inline ones.
 *
 * Tiled writes external tilesets as `{ firstgid, source }` once they are
 * shared across maps, which they are the moment there is a second level. The
 * parser refuses an unresolved one rather than guessing, so this is where the
 * fetching happens — and it keeps `firstgid` from the map, since that belongs
 * to the map and not to the tileset.
 */
async function resolveTilesets(map: TmjMap, from: string): Promise<TmjTileset[]> {
  return Promise.all(
    map.tilesets.map(async (tileset) => {
      if (!tileset.source) return tileset;
      const url = new URL(tileset.source, from).toString();
      const response = await fetch(url);
      if (!response.ok) {
        throw new LevelParseError(`tileset ${tileset.source} did not load (${response.status})`);
      }
      const resolved = (await response.json()) as TmjTileset;
      return { ...resolved, firstgid: tileset.firstgid, source: undefined };
    })
  );
}

/**
 * Parse and check a map already in hand.
 *
 * The half of `loadLevel` that is not the fetch, split out so a map that
 * arrived some other way — the dev panel's file picker, for a level nobody has
 * shipped yet — goes through exactly the same parser and the same guard.
 * `from` is what external tilesets resolve against; a file off disk names the
 * shipped `tilesets/chess.tsj` and finds it under `public/levels/` like any
 * shipped map does.
 *
 * A level that will not start beats a run that soft-locks twenty minutes in,
 * so the validation is not optional and the message names every fault at once.
 */
export async function loadLevelMap(map: TmjMap, from: string, name: string): Promise<LevelSetup> {
  return checked(parseLevel({ ...map, tilesets: await resolveTilesets(map, from) }), name);
}

function checked(level: LevelSetup, name: string): LevelSetup {
  const faults = validateLevel(level);
  if (faults.length > 0) {
    throw new LevelParseError(
      `level ${name} is malformed:\n  ${faults.map(faultMessage).join("\n  ")}`
    );
  }
  return level;
}

/** Fetch a level's fragments, place each, and check the whole (`campaign/fragments.ts`). */
export async function loadComposedLevel(head: ComposedLevel): Promise<LevelSetup> {
  const parts = await Promise.all(
    head.fragments.map(async (at) => {
      const url = `${base()}${fragmentPath(at.fragment)}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new LevelParseError(`fragment ${at.fragment} did not load (${response.status})`);
      }
      const map = placeFragment((await response.json()) as TmjMap, at);
      return parseLevel({ ...map, tilesets: await resolveTilesets(map, url) });
    })
  );
  return checked(composeLevel(head, parts), head.levelId);
}

/** The level a kind of run opens on, or undefined for a kind with none. */
export function loadRunKindLevel(kind: RunKind): Promise<LevelSetup | undefined> {
  const row = RUN_CONFIG[kind];
  if (row.compose) return loadComposedLevel(row.compose);
  if (row.level !== undefined) return loadLevel(row.level);
  return Promise.resolve(undefined);
}

/** Load a shipped level and check it before anybody plays it. */
export async function loadLevel(id: LevelId): Promise<LevelSetup> {
  const url = `${base()}${id}.tmj`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new LevelParseError(`level ${id} did not load (${response.status})`);
  }
  return loadLevelMap((await response.json()) as TmjMap, url, id);
}

/**
 * A level a run may be opened on: a shipped one by id, or a map somebody
 * handed over — the dev panel's file picker, for a `.tmj` still being drawn.
 *
 * `name` is the file's, for the error that names it; the level's own
 * `levelName` is what the shelf shows once it has parsed.
 */
export type LevelSource = LevelId | { name: string; map: TmjMap };

/** `loadLevel` for either kind of source. */
export function loadLevelSource(source: LevelSource): Promise<LevelSetup> {
  if (typeof source === "string") return loadLevel(source);
  return loadLevelMap(source.map, base(), source.name);
}
